import { useMemo } from 'react';
import { Box, Checkbox, TextField, Typography } from '@material-ui/core';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import { Autocomplete } from '@material-ui/lab';
import { parseEntityRef } from '@backstage/catalog-model';
import {
  humanizeEntityRef,
  useEntityList,
} from '@backstage/plugin-catalog-react';
import {
  AwsAccountAnnotationCsvFilter,
  AwsAccountFilters,
  AwsAccountSystemFilter,
  annotationMembers,
  systemRefsOf,
} from './filters';

type Option = { value: string; label: string };

const checkedIcon = <CheckBoxIcon fontSize="small" />;
const uncheckedIcon = <CheckBoxOutlineBlankIcon fontSize="small" />;

/** A labelled multi-select autocomplete facet with checkbox options. */
function MultiSelectFacet(props: {
  label: string;
  options: Option[];
  selected: string[];
  onChange: (values: string[]) => void;
}) {
  const { label, options, selected, onChange } = props;
  const byValue = useMemo(
    () => new Map(options.map(o => [o.value, o])),
    [options],
  );
  const value = selected
    .map(v => byValue.get(v))
    .filter((o): o is Option => Boolean(o));

  return (
    <Box pb={1} pt={1}>
      <Typography variant="button" component="label">
        {label}
      </Typography>
      <Autocomplete<Option, true>
        multiple
        disableCloseOnSelect
        options={options}
        value={value}
        getOptionLabel={o => o.label}
        getOptionSelected={(o, v) => o.value === v.value}
        onChange={(_, selectedOptions) =>
          onChange(selectedOptions.map(o => o.value))
        }
        renderOption={(o, { selected: isSelected }) => (
          <>
            <Checkbox
              icon={uncheckedIcon}
              checkedIcon={checkedIcon}
              checked={isSelected}
            />
            {o.label}
          </>
        )}
        size="small"
        popupIcon={undefined}
        renderInput={params => (
          <TextField {...params} variant="outlined" size="small" />
        )}
      />
    </Box>
  );
}

/** Distinct, sorted options built from a value-extractor over all loaded entities. */
function useDistinctOptions(
  extract: (entity: Parameters<typeof systemRefsOf>[0]) => string[],
  toLabel: (value: string) => string,
): Option[] {
  const { backendEntities } = useEntityList<AwsAccountFilters>();
  return useMemo(() => {
    const values = new Set<string>();
    for (const entity of backendEntities) {
      for (const v of extract(entity)) values.add(v);
    }
    return [...values]
      .sort((a, b) => a.localeCompare(b))
      .map(value => ({ value, label: toLabel(value) }));
  }, [backendEntities, extract, toLabel]);
}

/** A member that is a `user:default/*` (etc.) ref is humanized; a raw email is left as-is. */
function labelForMember(value: string): string {
  try {
    return humanizeEntityRef(parseEntityRef(value));
  } catch {
    return value;
  }
}

export function AwsAccountSystemPicker() {
  const { filters, updateFilters } = useEntityList<AwsAccountFilters>();
  const options = useDistinctOptions(systemRefsOf, value =>
    humanizeEntityRef(parseEntityRef(value)),
  );
  const selected = filters.awsSystem?.values ?? [];
  return (
    <MultiSelectFacet
      label="System"
      options={options}
      selected={selected}
      onChange={values =>
        updateFilters({
          awsSystem: values.length
            ? new AwsAccountSystemFilter(values)
            : undefined,
        })
      }
    />
  );
}

/** Shared picker for the three comma-joined owner-role annotations. */
function OwnerRolePicker(props: {
  label: string;
  annotationKey: string;
  name: 'awsBusinessOwner' | 'awsTechOwner' | 'awsProjectManager';
}) {
  const { label, annotationKey, name } = props;
  const { filters, updateFilters } = useEntityList<AwsAccountFilters>();
  const options = useDistinctOptions(
    entity => annotationMembers(entity, annotationKey),
    labelForMember,
  );
  const selected = filters[name]?.values ?? [];
  return (
    <MultiSelectFacet
      label={label}
      options={options}
      selected={selected}
      onChange={values =>
        updateFilters({
          [name]: values.length
            ? new AwsAccountAnnotationCsvFilter(annotationKey, values)
            : undefined,
        })
      }
    />
  );
}

export function AwsAccountBusinessOwnerPicker() {
  return (
    <OwnerRolePicker
      label="Business owner"
      annotationKey="temabit.com/business-owners"
      name="awsBusinessOwner"
    />
  );
}

export function AwsAccountTechOwnerPicker() {
  return (
    <OwnerRolePicker
      label="Tech owner"
      annotationKey="temabit.com/technical-owners"
      name="awsTechOwner"
    />
  );
}

export function AwsAccountProjectManagerPicker() {
  return (
    <OwnerRolePicker
      label="Project manager"
      annotationKey="temabit.com/project-managers"
      name="awsProjectManager"
    />
  );
}
